import React, { useState, useRef } from "react";
import { useClickOutside } from "../../../hooks/useClickOutside";

interface FilterState {
    status?: string;
}

interface UserFilterDropdownProps {
    onClose: () => void;
    onFilterChange: (filters: FilterState) => void;
    isOpen: boolean; // Add a prop to control visibility
}

const UserFilterDropdown: React.FC<UserFilterDropdownProps> = ({ 
    onClose, 
    onFilterChange, 
    isOpen 
}) => {
    const [filters, setFilters] = useState<FilterState>({ status: "" });
    const filterRef = useRef<HTMLDivElement | null>(null);

    // Detect clicks outside the filter container (only while open)
    useClickOutside(filterRef, onClose, isOpen);

    const handleInputChange = (field: keyof FilterState, value: string) => {
        const newFilters = { ...filters, [field]: value };
        setFilters(newFilters);
    };

    const clearAllFilters = () => {
        const clearedFilters = { status: "" };
        setFilters(clearedFilters);
        onFilterChange(clearedFilters);
        onClose();
    };

    // If not open, return null
    if (!isOpen) return null;

    return (
        <div
            ref={filterRef}
            className="fixed inset-x-4 top-1/2 -translate-y-1/2 z-20 w-auto bg-white divide-y divide-gray-100 rounded-lg shadow-lg sm:absolute sm:inset-x-auto sm:top-full sm:right-0 sm:left-auto sm:translate-y-0 sm:mt-2 sm:w-96"
        >
            <ul className="px-3 text-sm text-gray-700" aria-labelledby="dropdownDefaultButton-3">
                <div className="flex items-center justify-end pt-3 mx-3 rounded-t border-b border-lightgray-100 pb-4">
                    <button onClick={onClose} aria-label="Close">
                        <svg className="w-3 h-3" viewBox="0 0 14 14">
                            <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6" />
                        </svg>
                    </button>
                </div>

                {/* Status */}
                <div className="bg-indigo-200 bg-opacity-25 rounded-lg mt-4 p-4 mx-3 mb-4 border-b border-lightgray-100">
                    <p className="text-sm">Select Status</p>
                    <div className="flex sm:flex-nowrap flex-wrap items-center gap-4 mt-3">
                        <div className="w-full">
                            <select
                                value={filters.status}
                                onChange={(e) => handleInputChange("status", e.target.value)}
                                className="block w-full border-none p-3 rounded-lg bg-white text-xs text-grease-100 placeholder-gray-400 focus:outline-none"
                            >
                                <option value="">All</option>
                                <option value="Active">Active</option>
                                <option value="Blocked">Blocked</option>
                            </select>
                        </div>
                    </div>
                </div>

                {/* Actions */}
                <div className="flex items-center justify-between mx-3 mb-4">
                    <button onClick={onClose} className="flex gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 transition-colors rounded-md">
                        <p className="font-medium text-lightgray-200 text-xs">Cancel</p>
                    </button>
                    <div className="flex items-center gap-3">
                        <button onClick={clearAllFilters} className="flex gap-2 px-3 py-2 border border-[#FFBC00] rounded-md hover:bg-yellow-50 transition-colors">
                            <p className="font-medium text-[#FFBC00] text-xs">Clear All</p>
                        </button>
                        <button onClick={() => onFilterChange(filters)} className="flex gap-2 px-3 py-2 bg-[#FFBC00] hover:opacity-90 transition-opacity rounded-md">
                            <p className="font-medium text-white text-xs">Apply</p>
                        </button>
                    </div>
                </div>
            </ul>
        </div>
    );
};

export default UserFilterDropdown;